import { Fragment, useState } from "react"
import { CreateFoodWrapper } from "./styled"
import Header from "components/Layouts/Header"
import UpdateFoodForm from "./UpdateFoodForm"
import Button from "components/Buttons/Button"
import { useUpdateMyFood } from "../hooks"
import {
  useInitFoodImage,
  useResetFoodImage,
  useUploadedImagePayload,
} from "features/image/hooks"
import { generatePath, useNavigate } from "react-router-dom"
import PATHS from "router/paths"
import PopConfirm from "components/PopConfirm"
import { useAppSelector } from "app/hooks"
import { useMyFoodDetails } from "../hooks/myFood/useMyFoodDetails"
import FullPageLoading from "components/Layouts/FullPageLoading"
import { useSetPageTitle } from "libs/ga"
import { IUpdateMyFoodPayload } from "model/food/types/foodPayload.types"
import useMyFoodForm from "../hooks/myFood/useMyFoodForm"
import EdamaFoodDetails from "components/foodDetails/EdamaFoodDetails"
import {
  IEdamaIngredient,
  IEdamaIngredientOption,
} from "model/food/types/edama.types"
import SearchIngredients from "../CreateMyFood/SearchIngredients"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"

enum UpdateFoodSection {
  UpdateFoodForm = "UpdateFoodForm",
  SearchIngredients = "SearchIngredients",
  FoodIngredientDetails = "FoodIngredientDetails",
}

const UpdateMyFood = () => {
  useSetPageTitle("Edit Food")
  const { foodDetails, getFoodDetailsLoading } = useMyFoodDetails()
  useInitFoodImage(foodDetails)
  useResetFoodImage()
  const [isPopConfirmDiscardOpen, setIsPopConfirmDiscardOpen] = useState(false)
  const navigate = useNavigate()

  const updateMyFoodFormProps = useMyFoodForm(foodDetails)

  const {
    onAddIngredient,
    myFood,
    onRemoveIngredient,
    onUpdateIngredient,
    onPortionChange,
  } = updateMyFoodFormProps

  const { handleUpdateFood, updateFoodLoading } = useUpdateMyFood()
  const imagePayload = useUploadedImagePayload()
  const processImageLoading = useAppSelector(
    (state) => state.image.processImageLoading,
  )

  const onSubmitUpdateFood = () => {
    if (!myFood) return

    let payload: IUpdateMyFoodPayload = {
      ...myFood,
      source: "MY_FOOD",
      id: foodDetails!.id as string,
      ingredients: myFood.ingredients.map(({ uuid, ...rest }) => {
        const ingredient: IEdamaIngredient = {
          ...rest,
        }

        return ingredient
      }),
    }

    if (imagePayload) {
      payload = { ...payload, ...imagePayload }
    }

    handleUpdateFood(payload)
  }

  const [isHideHeader, setIsHideHeader] = useState(false)
  const [section, setSection] = useState<UpdateFoodSection>(
    UpdateFoodSection.UpdateFoodForm,
  )

  const [ingredientDetails, setIngredientDetails] =
    useState<null | IEdamaIngredientOption>(null)

  const showSearchIngredients = () => {
    setSection(UpdateFoodSection.SearchIngredients)
  }

  const backToDetails = () => {
    navigate(generatePath(PATHS.app.food.details, { id: foodDetails?.id }))
  }

  const onBack = () => {
    if (section !== UpdateFoodSection.UpdateFoodForm) {
      setSection(UpdateFoodSection.UpdateFoodForm)
    } else {
      setIsPopConfirmDiscardOpen(true)
    }
  }

  const onViewIngredientDetails = (ingredient: IEdamaIngredientOption) => {
    setSection(UpdateFoodSection.FoodIngredientDetails)
    setIngredientDetails(ingredient)
  }

  const [isPortionValid, setIsPortionValid] = useState(true)

  const handlePortionChange = (value: number) => {
    onPortionChange(value)
    setIsPortionValid(true)
  }

  const shouldShowHeader =
    !isHideHeader && section !== UpdateFoodSection.FoodIngredientDetails

  const isFormEmpty = myFood?.ingredients.length === 0 && !myFood?.name
  const isDisabledSubmit = isFormEmpty || processImageLoading || !isPortionValid

  return (
    <Fragment>
      {getFoodDetailsLoading || !myFood ? (
        <FullPageLoading />
      ) : (
        <CreateFoodWrapper
          $background={
            section === UpdateFoodSection.UpdateFoodForm ? "#f5f7f7" : "#fff"
          }
        >
          {shouldShowHeader && (
            <Header
              isAppPaddingStyle
              title="Edit Meal"
              onBack={onBack}
              disabled={updateFoodLoading}
            />
          )}

          {section === UpdateFoodSection.UpdateFoodForm && myFood! && (
            <Fragment>
              <UpdateFoodForm
                showSearchIngredients={showSearchIngredients}
                onViewIngredientDetails={onViewIngredientDetails}
                {...updateMyFoodFormProps}
                onPortionChange={handlePortionChange}
                food={myFood!}
                setIsPortionValid={setIsPortionValid}
              />
              <AppStyleCtaBox>
                <Button
                  disabled={isDisabledSubmit}
                  loading={updateFoodLoading}
                  onClick={onSubmitUpdateFood}
                  style={{ width: "100%" }}
                  $type="primary"
                >
                  {" "}
                  Save 
                </Button>
              </AppStyleCtaBox>
            </Fragment>
          )}

          {section === UpdateFoodSection.FoodIngredientDetails &&
            ingredientDetails && (
              <EdamaFoodDetails
                isIngredient
                food={ingredientDetails}
                onBack={() => {
                  setSection(UpdateFoodSection.UpdateFoodForm)
                }}
                onEdit={(food) => onUpdateIngredient(food)}
                onDelete={() => {
                  onRemoveIngredient(ingredientDetails.uuid)
                  setSection(UpdateFoodSection.UpdateFoodForm)
                }}
                mode="edit"
              />
            )}

          {section === UpdateFoodSection.SearchIngredients && (
            <SearchIngredients
              setIsHideHeader={setIsHideHeader}
              onAddIngredient={onAddIngredient}
            />
          )}

          <PopConfirm
            isOpen={isPopConfirmDiscardOpen}
            title="Unsaved changes?"
            cancelText="Discard"
            confirmText="Stay"
            onCancel={backToDetails}
            onConfirm={() => {
              setIsPopConfirmDiscardOpen(false)
            }}
            loading={false}
          >
            <div style={{ textAlign: "center" }}>
              You haven't saved your meal yet. Are you sure you want to leave?
            </div>
          </PopConfirm>
        </CreateFoodWrapper>
      )}
    </Fragment>
  )
}

export default UpdateMyFood
